import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import TabNavigation from '../components/TabNavigation';
import { formatDate, formatDateTime, todayLocalISO } from '../utils/dateUtils';
import { mapHazardToIncidentType } from '../utils/hazardTypes';
import { severityToLabel, severityBadgeClass } from '../utils/severity';
import { allowedTransitions } from '../constants/incidentStates';
import PDFExportModal from '../components/PDFExportModal';
import FiveWhyAnalysis from '../components/FiveWhyAnalysis';
import FishboneAnalysis from '../components/FishboneAnalysis';
import WhyWhyAnalysis from '../components/WhyWhyAnalysis';
import StatutoryReportForm from '../components/StatutoryReportForm';
import { AuditTrailViewer } from '../components/AuditTrail';
import EightDReport from '../components/EightDReport';
import { useAlert } from '../context/AlertContext';
import { useSite } from '../context/SiteContext';
import { canApprove } from '../utils/roleUtils';
import { usePermissions } from '../context/PermissionContext';
import { OBSERVATION_CATEGORIES, observationCategoryLabel } from '../constants/observationCategories';
import { INCIDENT_TYPE_HELP } from '../constants/incidentTypes';
import {
  useTenantFieldConfig,
  anyRcaFieldShown as anyRcaFieldShownFor,
  isRcaAiAnalysisPending,
  formatShownRcaLabels,
} from '../hooks/useTenantFieldConfig';

const ROOT_CAUSE_TAGS = [
  'Equipment', 'PPE', 'Training', 'Procedures', 'Human Factors',
  'Environmental', 'Housekeeping', 'Communication', 'Supervision',
  'Maintenance', 'Design', 'Management System'
];

// `entity` selects which record family this detail page renders:
//   'incident'    (default) — /api/incidents, unchanged legacy behavior
//   'observation'            — /api/observations (UA/UC), injury/statutory
//                              UI hidden, category instead of incident type
const HazardDetail = ({ user, onLogout, entity = 'incident' }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isObservation = entity === 'observation';
  // All record-scoped API calls below dispatch through apiBase so the same
  // component serves both /incidents/* and /observations/* backends.
  const apiBase = isObservation ? `${API}/observations` : `${API}/incidents`;
  // RBAC resource for UI gating (backend enforces the same resource).
  const permResource = isObservation ? 'observations' : 'incidents';
  const listPath = isObservation ? '/observations' : '/incidents';
  const entityLabel = isObservation ? 'Observation' : 'Incident';
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const { hasPermission } = usePermissions();
  const [hazard, setHazard] = useState(null);
  const [loading, setLoading] = useState(true);
  // Tenant-configured visibility of Root Cause / Corrective Action /
  // Recommended Future Actions (all shown unless the tenant switched one off).
  const rcaFields = useTenantFieldConfig();
  const anyRcaFieldShown = anyRcaFieldShownFor(rcaFields);
  const rcaAiPending = isRcaAiAnalysisPending(hazard, rcaFields);
  const shownRcaLabels = formatShownRcaLabels(rcaFields);
  // Inline-edit mode is removed — EDIT now redirects to the create-incident
  // form (/report?draftId=<id>) so all flows share one editable surface.
  // Existing `editing ? <input> : <display>` branches remain as read-only
  // display only.
  const editing = false;
  const [exportingPDF, setExportingPDF] = useState(false);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [showFiveWhy, setShowFiveWhy] = useState(false);
  const [showFishbone, setShowFishbone] = useState(false);
  const [showWhyWhy, setShowWhyWhy] = useState(false);
  const [showStatutoryForm, setShowStatutoryForm] = useState(null); // {family, rows} of the chosen form_family
  const [siteCountry, setSiteCountry] = useState(null);
  const [statutoryForms, setStatutoryForms] = useState([]); // jurisdiction-resolved form entries
  const [countryLoading, setCountryLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('details'); // 'details' or 'audit'
  const [photoUrls, setPhotoUrls] = useState([]); // [{url, object_key}, ...]
  const [photoLoading, setPhotoLoading] = useState(false);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [deletingPhoto, setDeletingPhoto] = useState(null);
  const [users, setUsers] = useState([]);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [showClosureModal, setShowClosureModal] = useState(false);
  const [closureNotes, setClosureNotes] = useState('');
  const [closureFiles, setClosureFiles] = useState([]);
  const [closingIncident, setClosingIncident] = useState(false);
  const [docUploading, setDocUploading] = useState(false);
  const [eightDUploading, setEightDUploading] = useState(false);
  const [docDeleting, setDocDeleting] = useState(null);
  const [closureFileUrls, setClosureFileUrls] = useState([]);
  const [linkedCapas, setLinkedCapas] = useState([]);
  const [showLinkCapaModal, setShowLinkCapaModal] = useState(false);
  const [availableCapas, setAvailableCapas] = useState([]);
  const [linkingCapa, setLinkingCapa] = useState(false);
  const { activeSiteId } = useSite();
  const isCrossSiteReadOnly = hazard?.read_only === true;
  const isAdminOrHSE = user?.role === 'admin' || user?.role === 'Admin' || user?.role === 'hse_manager' || user?.role === 'HSE Manager';

  useEffect(() => {
    fetchHazard();
  }, [id, activeSiteId]);

  // Assignee options are scoped to the record's own department, so the edit
  // dropdown only offers Safety Managers who actually own it. Keyed on the
  // loaded record because the department isn't known until the fetch resolves.
  useEffect(() => {
    if (!isAdminOrHSE) return;
    fetchUsers(hazard?.department_id);
  }, [isAdminOrHSE, activeSiteId, hazard?.department_id]);

  // Fetch country for site to determine which statutory report buttons to show
  useEffect(() => {
    // Statutory reports are incident-only; skip the lookup for observations.
    if (isObservation) return;
    if (!hazard?.site_id) return;
    const fetchCountry = async () => {
      setCountryLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/org/nodes/${hazard.site_id}/country`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSiteCountry(res.data?.country_name || null);
        setStatutoryForms(res.data?.statutory_forms || []);
      } catch {
        setSiteCountry(null);
        setStatutoryForms([]);
      } finally {
        setCountryLoading(false);
      }
    };
    fetchCountry();
  }, [hazard?.site_id]);

  // Safety Managers eligible to own this record. Without a department the
  // endpoint returns every Safety Manager in the tenant, which is the right
  // behaviour for legacy records that predate the department field.
  const fetchUsers = async (departmentId) => {
    try {
      const params = new URLSearchParams();
      if (departmentId) params.append('department_id', departmentId);
      if (activeSiteId) params.append('site_id', activeSiteId);
      const qs = params.toString();
      const response = await axios.get(
        `${API}/incidents/eligible-assignees${qs ? `?${qs}` : ''}`
      );
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching eligible assignees:', error);
      setUsers([]);
    }
  };

  const fetchClosureFiles = async () => {
    try {
      const response = await axios.get(`${apiBase}/${id}/closure-files`);
      setClosureFileUrls(response.data.files || []);
    } catch (error) {
      // Not critical — closure files may not exist
      setClosureFileUrls([]);
    }
  };

  const fetchLinkedCapas = async () => {
    try {
      const response = await axios.get(`${API}/capa-links/${entity}/${id}`);
      setLinkedCapas(response.data || []);
    } catch (error) {
      setLinkedCapas([]);
    }
  };

  const fetchAvailableCapas = async () => {
    try {
      const response = await axios.get(`${API}/capa`);
      const linkedIds = linkedCapas.map(c => c.id);
      setAvailableCapas((response.data || []).filter(c => !linkedIds.includes(c.id)));
    } catch (error) {
      setAvailableCapas([]);
    }
  };

  const handleLinkCapa = async (capaId) => {
    setLinkingCapa(true);
    try {
      await axios.post(`${API}/capa-links`, {
        capa_id: capaId,
        source_reference_type: entity,
        source_reference_id: id,
      });
      showSuccess(`CAPA linked to ${entityLabel.toLowerCase()}`);
      setShowLinkCapaModal(false);
      fetchLinkedCapas();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to link CAPA');
    } finally {
      setLinkingCapa(false);
    }
  };

  const handleUnlinkCapa = async (capaId, linkId) => {
    if (!linkId) {
      showError('Cannot unlink a legacy-linked CAPA. Edit the CAPA directly to change its source.');
      return;
    }
    try {
      await axios.delete(`${API}/capa-links/${linkId}`);
      showSuccess('CAPA unlinked');
      fetchLinkedCapas();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to unlink CAPA');
    }
  };

  const fetchHazard = async () => {
    try {
      const response = await axios.get(`${apiBase}/${id}`);
      // Backend stores severity in enum form (minor/moderate/major/...).
      // The form's dropdown and the colour-coded badge both expect the
      // friendly label, so normalise once on entry. Backend accepts both
      // forms on save.
      const normalised = {
        ...response.data,
        severity: severityToLabel(response.data?.severity),
      };
      setHazard(normalised);
      setFormData(normalised);
      // Fetch photo URL after loading hazard
      fetchPhotoUrl();
      // Fetch closure file URLs if incident is closed
      if (response.data.status === 'Closed' && response.data.closure_attachments?.length > 0) {
        fetchClosureFiles();
      }
      // Fetch linked CAPAs
      fetchLinkedCapas();
    } catch (error) {
      console.error('Failed to fetch hazard:', error);
      showError(`Failed to load ${entityLabel.toLowerCase()} details`);
      navigate(listPath);
    } finally {
      setLoading(false);
    }
  };

  const fetchPhotoUrl = async () => {
    setPhotoLoading(true);
    try {
      const response = await axios.get(`${apiBase}/${id}/photos`);
      if (response.data.photos && response.data.photos.length > 0) {
        setPhotoUrls(response.data.photos.map(p => ({ url: p.photo_url, object_key: p.object_key })));
      } else {
        setPhotoUrls([]);
      }
    } catch (error) {
      // 404 means no photos - this is not an error
      if (error.response?.status !== 404) {
        console.error('Failed to fetch photos:', error);
      }
      setPhotoUrls([]);
    } finally {
      setPhotoLoading(false);
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Check total file count (existing uploaded + pending new + newly selected)
    const totalPhotos = photoUrls.length + photoFiles.length + files.length;
    if (totalPhotos > 10) {
      showError(`Maximum 10 photos allowed. You already have ${photoUrls.length} uploaded and ${photoFiles.length} pending.`);
      return;
    }

    const validFiles = [];
    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        showError(`${file.name}: Image size should be less than 10MB`);
        return;
      }
      validFiles.push(file);
    });

    // Create previews for valid files
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreviews(prev => [...prev, { name: file.name, preview: reader.result }]);
      };
      reader.readAsDataURL(file);
    });

    setPhotoFiles(prev => [...prev, ...validFiles]);
  };

  const removePhotoPreview = (index) => {
    setPhotoFiles(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handlePhotoSave = async () => {
    if (photoFiles.length === 0) return;

    setUploadingPhoto(true);
    try {
      const photoFormData = new FormData();
      photoFiles.forEach(file => {
        photoFormData.append('files', file);
      });

      await axios.post(`${apiBase}/${id}/upload-photos`, photoFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      showSuccess(`${photoFiles.length} photo(s) uploaded successfully!`);
      setPhotoFiles([]);
      setPhotoPreviews([]);
      // Refresh the photo URLs
      await fetchPhotoUrl();
    } catch (error) {
      console.error('Photo upload error:', error);
      showError('Failed to upload photos. Please try again.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleCancelPhotoUpload = () => {
    setPhotoFiles([]);
    setPhotoPreviews([]);
  };

  const handleDeletePhoto = async (objectKey) => {
    const confirmed = await confirm('Are you sure you want to delete this photo? This action cannot be undone.');
    if (!confirmed) return;

    setDeletingPhoto(objectKey);
    try {
      await axios.delete(`${apiBase}/${id}/photos`, {
        data: { object_key: objectKey }
      });
      showSuccess('Photo deleted successfully');
      await fetchPhotoUrl();
    } catch (error) {
      console.error('Photo delete error:', error);
      showError(error.response?.data?.detail || 'Failed to delete photo');
    } finally {
      setDeletingPhoto(null);
    }
  };

  const handleDocUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const existingDocs = hazard?.documents || [];
    if (existingDocs.length + files.length > 20) {
      showError(`Maximum 20 documents allowed. Currently ${existingDocs.length} uploaded.`);
      return;
    }

    setDocUploading(true);
    try {
      const formDataUpload = new FormData();
      Array.from(files).forEach(f => formDataUpload.append('files', f));

      const response = await axios.post(`${apiBase}/${id}/documents`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.errors?.length) {
        showError(`Some files failed: ${response.data.errors.join(', ')}`);
      } else {
        showSuccess(`${response.data.documents?.length || 0} document(s) uploaded`);
      }
      await fetchHazard();
    } catch (error) {
      console.error('Document upload error:', error);
      showError(error.response?.data?.detail || 'Failed to upload documents');
    } finally {
      setDocUploading(false);
      e.target.value = '';
    }
  };

  const handleDocDelete = async (index) => {
    const confirmed = await confirm('Delete this document? This cannot be undone.');
    if (!confirmed) return;

    setDocDeleting(index);
    try {
      await axios.delete(`${apiBase}/${id}/documents/${index}`);
      showSuccess('Document deleted');
      await fetchHazard();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to delete document');
    } finally {
      setDocDeleting(null);
    }
  };

  const handleDocDownload = async (index, filename) => {
    try {
      const response = await axios.get(`${apiBase}/${id}/documents/${index}/download`);
      window.open(response.data.url, '_blank');
    } catch (error) {
      showError('Failed to get download link');
    }
  };

  // --- 8D Report Document upload/download ---
  const eightDReportDocs = (hazard?.documents || [])
    .map((doc, idx) => ({ ...doc, _docIndex: idx }))
    .filter(doc => doc.eight_d_section === '8d_report');

  const handleEightDUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setEightDUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach(f => fd.append('files', f));
      fd.append('eight_d_section', '8d_report');
      const response = await axios.post(`${apiBase}/${id}/documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.errors?.length) {
        showError(`Some files failed: ${response.data.errors.join(', ')}`);
      } else {
        showSuccess('8D report uploaded');
      }
      await fetchHazard();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to upload 8D report');
    } finally {
      setEightDUploading(false);
      e.target.value = '';
    }
  };

  const handleEightDDownload = async (docIndex) => {
    try {
      const response = await axios.get(`${apiBase}/${id}/documents/${docIndex}/download`);
      window.open(response.data.url, '_blank');
    } catch (error) {
      showError('Failed to get download link');
    }
  };

  const handleEightDDelete = async (docIndex) => {
    const confirmed = await confirm('Delete this 8D report document?');
    if (!confirmed) return;
    try {
      await axios.delete(`${apiBase}/${id}/documents/${docIndex}`);
      showSuccess('8D report document deleted');
      await fetchHazard();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const getDocIcon = (fileType) => {
    switch (fileType) {
      case 'pdf': return '📄';
      case 'doc': case 'docx': return '📝';
      case 'xls': case 'xlsx': return '📊';
      case 'ppt': case 'pptx': return '📑';
      case 'jpg': case 'jpeg': case 'png': case 'gif': return '🖼️';
      default: return '📎';
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Keep is_lti mirrored to the incident type — ticked for "Lost Time
    // Injury", cleared when switching away (so the flag never goes stale).
    if (name === 'hazard_type') {
      setFormData(prev => ({ ...prev, [name]: value, is_lti: value === 'Lost Time Injury' }));
    } else if (name === 'injury_count') {
      // Resize the per-person LTI array to match the new count so the two
      // always submit consistently. Confirm before dropping non-zero entries.
      setFormData(prev => {
        const n = parseInt(value) || 0;
        const current = Array.isArray(prev.injured_persons) ? prev.injured_persons : [];
        let next = current;
        if (n > current.length) {
          next = [...current, ...Array.from({ length: n - current.length }, () => ({ lti_days: 0 }))];
        } else if (n < current.length) {
          const dropped = current.slice(n);
          const hasData = dropped.some(p => Number(p?.lti_days) > 0);
          if (hasData && !window.confirm(
            `Reducing the injury count will drop ${dropped.length} recorded LTI entr${dropped.length === 1 ? 'y' : 'ies'}. Continue?`
          )) {
            return prev; // keep count where it was
          }
          next = current.slice(0, n);
        }
        const total = next.reduce((s, p) => s + (Number(p?.lti_days) || 0), 0);
        return {
          ...prev,
          injury_count: value,
          injured_persons: next,
          medical_leave_days: next.length > 0 ? String(total) : prev.medical_leave_days,
        };
      });
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleInjuredPersonChange = (index, value) => {
    setFormData(prev => {
      const next = (prev.injured_persons || []).map((p, i) =>
        i === index ? { ...p, lti_days: value } : p
      );
      const total = next.reduce((s, p) => s + (Number(p?.lti_days) || 0), 0);
      return {
        ...prev,
        injured_persons: next,
        medical_leave_days: next.length > 0 ? String(total) : prev.medical_leave_days,
      };
    });
  };

  const handleGenerateAIAnalysis = async () => {
    setGeneratingAI(true);
    try {
      const response = await axios.post(`${apiBase}/${id}/generate-ai-analysis`);
      await fetchHazard();
      const updated = response.data?.fields_updated;
      if (updated && updated.length > 0) {
        showSuccess(`AI analysis applied — updated: ${updated.join(', ')}.`);
      } else {
        showInfo(response.data?.message || 'AI analysis ran but found no new fields to update.');
      }
    } catch (error) {
      console.error('Failed to generate AI analysis:', error);
      showError(error.response?.data?.detail || 'Failed to generate AI analysis. Please try again.');
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleMarkClosed = () => {
    setShowClosureModal(true);
  };

  const handleClosureSubmit = async () => {
    setClosingIncident(true);
    try {
      // Step 1: Upload closure files if any
      let uploadedKeys = [];
      if (closureFiles.length > 0) {
        const formData = new FormData();
        closureFiles.forEach(file => formData.append('files', file));
        const uploadRes = await axios.post(`${apiBase}/${id}/closure-files`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploadedKeys = uploadRes.data.uploaded_keys || [];
      }

      // Step 2: Close the incident with notes and attachment keys
      const closePayload = { status: 'Closed' };
      if (closureNotes.trim()) closePayload.close_notes = closureNotes.trim();
      if (uploadedKeys.length > 0) closePayload.closure_attachments = uploadedKeys;

      await axios.put(`${apiBase}/${id}`, closePayload);
      setShowClosureModal(false);
      setClosureNotes('');
      setClosureFiles([]);
      await fetchHazard();
      showSuccess(`${entityLabel} has been successfully closed!`);
    } catch (error) {
      console.error(`Failed to close ${entityLabel.toLowerCase()}:`, error);
      if (error.response?.status === 401) {
        showError('Session expired. Please login again.');
        window.location.href = '/login';
      } else {
        showError(`Failed to close ${entityLabel.toLowerCase()}. Please try again.`);
      }
    } finally {
      setClosingIncident(false);
    }
  };

  const handleClosureFileChange = (e) => {
    const files = Array.from(e.target.files);
    const totalFiles = closureFiles.length + files.length;
    if (totalFiles > 5) {
      showError('Maximum 5 closure files allowed.');
      return;
    }
    const validFiles = files.filter(f => {
      if (f.size > 10 * 1024 * 1024) {
        showError(`${f.name}: File size exceeds 10MB limit`);
        return false;
      }
      return true;
    });
    setClosureFiles(prev => [...prev, ...validFiles]);
  };

  const removeClosureFile = (index) => {
    setClosureFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleExportPDF = async () => {
    setShowPDFModal(true);
    setExportingPDF(true);
    
    try {
      // Wait for the animation to complete (simulate processing time)
      // Total animation time from modal: ~7 seconds
      await new Promise(resolve => setTimeout(resolve, 6500));
      
      const response = await axios.get(`${API}/export/pdf/${id}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Saarv_Incident_Report_${id.substring(0, 8).toUpperCase()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      // Wait a bit more to show completion, then close modal
      await new Promise(resolve => setTimeout(resolve, 1000));
      setShowPDFModal(false);

      // Show success message
      setTimeout(() => {
        showSuccess('PDF report downloaded successfully!');
      }, 500);
    } catch (error) {
      console.error('Failed to export PDF:', error);
      setShowPDFModal(false);
      if (error.response?.status === 401) {
        showError('Session expired. Please login again.');
        window.location.href = '/login';
      } else {
        showError('Failed to export PDF. Please try again.');
      }
    } finally {
      setExportingPDF(false);
    }
  };

  // Replaced by severityBadgeClass from utils/severity which also handles
  // the stored enum form (minor / moderate / major / catastrophic / near_miss)
  // and the Critical bucket the previous switch was missing entirely.
  const getSeverityColor = severityBadgeClass;

  const handleStatutoryExport = (family, rows) => {
    // Open the data collection form for the chosen form family
    setShowStatutoryForm({ family, rows });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Closed':
        return 'bg-green-100 text-green-700 border-green-500';
      case 'Overdue':
        return 'bg-red-100 text-red-700 border-red-500';
      case 'In-Progress':
        return 'bg-blue-100 text-blue-700 border-blue-500';
      case 'Open':
        return 'bg-yellow-100 text-yellow-700 border-yellow-500';
      case 'Draft':
        return 'bg-gray-100 text-gray-600 border-gray-400';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#1B5C94]"></div>
        </div>
      </div>
      </div>
    );
  }

  if (!hazard) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-xl text-gray-600 mb-4">{entityLabel} not found</p>
            <button
              onClick={() => navigate(listPath)}
              className="bg-[#1B5C94] text-white px-6 py-2 rounded-lg hover:bg-[#3AC0DB]"
            >
              Back to {entityLabel}s List
            </button>
          </div>
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-6" data-testid="hazard-detail-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <button
              onClick={() => navigate(listPath)}
              className="text-[#1B5C94] hover:text-[#3AC0DB] font-semibold mb-2 flex items-center"
            >
              ← Back to {entityLabel}s List
            </button>
            <h1 className="text-3xl font-bold text-gray-800">{entityLabel.toUpperCase()} DETAILS</h1>
            {hazard?.site_name && (
              <p className="text-sm font-medium text-blue-700 mt-1">Site: {hazard.site_name}</p>
            )}
          </div>
          {isCrossSiteReadOnly && (
            <div className="bg-amber-50 border-l-4 border-amber-400 p-3 mb-4 col-span-full">
              <p className="text-sm text-amber-700">
                This record is from <strong>{hazard.site_name}</strong> and is read-only.
              </p>
            </div>
          )}
          <div className="mt-4 md:mt-0 flex flex-wrap gap-2">
            {/* BUG-282: Hide export from Viewers. Backend also enforces.
                Hidden for observations — no export endpoint in v1. */}
            {!isObservation && hasPermission(permResource, 'export') && (
              <button
                onClick={handleExportPDF}
                disabled={exportingPDF}
                className="bg-gradient-to-r from-[#DAA520] to-[#C47723] text-white font-bold py-2 px-4 rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
                data-testid="export-pdf-button"
              >
                {exportingPDF ? 'Exporting...' : '📊 Export PDF'}
              </button>
            )}
            {hasPermission(permResource, 'update') && hazard?.status !== 'Closed' && !isCrossSiteReadOnly && activeTab === 'details' && (
              <button
                onClick={() => navigate(isObservation ? `/observations/create?draftId=${id}` : `/report?draftId=${id}`)}
                className="bg-[#1B5C94] text-white font-bold py-2 px-4 rounded-lg hover:bg-[#3AC0DB] transition-colors"
                data-testid="edit-button"
              >
                EDIT
              </button>
            )}
            {/* QA #3: Draft records follow Draft → Open → In Progress → Closed;
                closure is only offered once submitted (past Draft). */}
            {hasPermission(permResource, 'approve') && hazard?.status !== 'Closed' && hazard?.status !== 'Draft' && !editing && (
              <button
                onClick={handleMarkClosed}
                className="bg-green-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
                data-testid="mark-closed-button"
              >
                MARK AS CLOSED
              </button>
            )}
            {/* QA #4: CAPA actions only make sense for submitted records. */}
            {hasPermission('capa', 'create') && hazard?.status !== 'Draft' && !editing && (
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const params = new URLSearchParams({
                      title: `CAPA for: ${hazard?.title || ''}`,
                      description: hazard?.description || '',
                      source: entity,
                      source_id: id,
                      severity: hazard?.severity || 'Medium',
                    });
                    navigate(`/capa/create?${params.toString()}`);
                  }}
                  className="bg-purple-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Create CAPA
                </button>
                {activeTab === 'details' && (
                  <button
                    onClick={() => { setShowLinkCapaModal(true); fetchAvailableCapas(); }}
                    className="bg-purple-100 text-purple-700 font-bold py-2 px-4 rounded-lg hover:bg-purple-200 transition-colors border border-purple-300"
                  >
                    Link Existing CAPA
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Closed Incident Banner */}
        {hazard?.status === 'Closed' && (
          <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 flex items-center gap-3" data-testid="closed-banner">
            <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <div>
              <p className="text-green-800 font-semibold">This {entityLabel.toLowerCase()} is closed and cannot be edited.</p>
              <p className="text-green-600 text-sm">
                Closed on {hazard.closed_date ? formatDateTime(hazard.closed_date) : 'N/A'}. To make changes, the {entityLabel.toLowerCase()} must be reopened by an authorized user.
              </p>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('details')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-[#1B5C94] text-[#1B5C94]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('audit')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'audit'
                  ? 'border-[#1B5C94] text-[#1B5C94]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Audit History
            </button>
            {/* 8D reports are an incident-investigation feature — no
                observation backend support, so the tab is hidden. */}
            {!isObservation && (
              <button
                onClick={() => setActiveTab('8d')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                  activeTab === '8d'
                    ? 'border-[#1B5C94] text-[#1B5C94]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                8D Report
              </button>
            )}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'details' && (
          <>
        {/* RCA Tools & Statutory Reports Row */}
        {hasPermission(permResource, 'investigate') && (
          <div className="bg-blue-50 rounded-xl p-4 flex flex-wrap gap-3">
            <h3 className="w-full text-sm font-bold text-gray-700 mb-2">🔍 Root Cause Analysis Tools</h3>
            
            <button
              onClick={() => setShowFiveWhy(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              5-Why Analysis
            </button>

            <button
              onClick={() => setShowFishbone(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              🐟 Fishbone Diagram
            </button>

            {/* Why-Why Tree — branching RCA, incidents only (the backend
                endpoint exists only under /api/incidents). Same permission
                gate as the 5-Why button: this whole box needs 'investigate'. */}
            {!isObservation && (
              <button
                onClick={() => setShowWhyWhy(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
                data-testid="why-why-tree-button"
              >
                🌳 Why-Why Tree
              </button>
            )}

            {/* 8D report + statutory report tooling is incident-only — no
                backend endpoints for observations, so hide the whole block. */}
            {!isObservation && (
            <>
            {/* Upload 8D Report — next to RCA tools */}
            <label className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors cursor-pointer ${
              eightDUploading
                ? 'bg-gray-300 text-gray-500 cursor-wait'
                : 'bg-teal-600 text-white hover:bg-teal-700'
            }`}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              {eightDUploading ? 'Uploading...' : 'Upload 8D Report'}
              <input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx"
                className="hidden"
                onChange={handleEightDUpload}
                disabled={eightDUploading || hazard.status === 'Closed'}
              />
            </label>

            {/* Uploaded 8D report files */}
            {eightDReportDocs.map((doc) => (
              <div key={doc._docIndex} className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm">
                <span>{getDocIcon(doc.file_type)}</span>
                <span className="font-medium text-gray-800 truncate max-w-[180px]" title={doc.filename}>{doc.filename}</span>
                {/* BUG-282: Download also requires incidents:export permission. */}
                {hasPermission(permResource, 'export') && (
                  <button onClick={() => handleEightDDownload(doc._docIndex)}
                    className="text-[#1B5C94] hover:text-[#3AC0DB] font-semibold ml-1">Download</button>
                )}
                {hazard.status !== 'Closed' && (
                  <button onClick={() => handleEightDDelete(doc._docIndex)}
                    className="text-red-500 hover:text-red-700 font-semibold">Delete</button>
                )}
              </div>
            ))}

            <div className="border-l-2 border-gray-300 mx-2"></div>

            <h3 className="w-full text-sm font-bold text-gray-700 mb-2 mt-2">Statutory Reports</h3>

            {countryLoading && (
              <span className="text-xs text-gray-400">Loading country-specific reports...</span>
            )}

            {/* BUG-282: Statutory exports also gated on incidents:export */}
            {/* Buttons render from the jurisdiction-resolved registry (org-node
                overrides > built-in defaults): one button per form_family. */}
            {hasPermission(permResource, 'export') && (() => {
              const families = [];
              statutoryForms.forEach((f) => {
                const g = families.find((x) => x.family === f.form_family);
                if (g) g.rows.push(f);
                else families.push({ family: f.form_family, rows: [f] });
              });
              const buttonColors = ['bg-orange-600 hover:bg-orange-700', 'bg-red-600 hover:bg-red-700', 'bg-blue-600 hover:bg-blue-700', 'bg-emerald-600 hover:bg-emerald-700'];
              return families.map(({ family, rows }, i) => (
                <div key={family} className="flex flex-col">
                  <button
                    onClick={() => handleStatutoryExport(family, rows)}
                    className={`flex items-center gap-2 px-4 py-2 ${buttonColors[i % buttonColors.length]} text-white rounded-lg transition-colors font-semibold`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {rows[0].group_label || rows[0].display_label}
                  </button>
                  {rows[0].is_fallback && (
                    <span className="text-[10px] text-amber-600 mt-0.5">country default — set the site's State in org hierarchy</span>
                  )}
                </div>
              ));
            })()}

            {!countryLoading && statutoryForms.length === 0 && (
              <span className="text-xs text-gray-400 italic">
                {siteCountry
                  ? 'No statutory reports configured for this site’s jurisdiction.'
                  : 'No country-specific reports available. Set up country in org hierarchy.'}
              </span>
            )}
            </>
            )}
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info Card */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-[#1B5C94] mb-4">Basic Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-bold text-gray-700">{isObservation ? 'Observation Title' : 'Incident Title'}</label>
                  {editing ? (
                    <input
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                      data-testid="edit-title-input"
                    />
                  ) : (
                    <p className="text-gray-800 font-semibold mt-1" data-testid="hazard-title">{hazard.title}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-bold text-gray-700">{isObservation ? 'Observation Category' : 'Incident Type'}</label>
                    {editing && isObservation ? (
                      <select
                        name="observation_category"
                        value={formData.observation_category || ''}
                        onChange={handleInputChange}
                        className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                        data-testid="edit-category-select"
                      >
                        <option value="">Select Observation Category</option>
                        {OBSERVATION_CATEGORIES.map(c => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    ) : editing ? (
                      <>
                      <select
                        name="hazard_type"
                        value={formData.hazard_type || ''}
                        onChange={handleInputChange}
                        className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                        data-testid="edit-type-select"
                      >
                        {/* Keep this list in sync with the create form
                            (ReportHazard.js). When the two diverge an
                            existing incident's hazard_type can fall outside
                            the edit dropdown's options — React's controlled
                            select then drops the value silently, the diff
                            comes out empty, and Save fails with 400 "No
                            fields to update". */}
                        <option value="">Select Incident Type</option>
                        {/* Observation / Unsafe Act / Unsafe Condition were
                            removed — they belong to the separate Observations
                            module. */}
                        <option value="Near-Miss">Near-Miss</option>
                        <option value="First Aid">First Aid</option>
                        <option value="Medical Treatment">Medical Treatment</option>
                        <option value="Restricted Work">Restricted Work</option>
                        <option value="Lost Time Injury">Lost Time Injury</option>
                        <option value="Fatality">Fatality</option>
                        <option value="Property Damage">Property Damage</option>
                        <option value="Environmental">Environmental</option>
                        <option value="Equipment Failure">Equipment Failure</option>
                        <option value="Occupational Disease">Occupational Disease</option>
                        <option value="Occupational Poisoning">Occupational Poisoning</option>
                        <option value="Other">Other</option>
                      </select>
                      {formData.hazard_type && INCIDENT_TYPE_HELP[formData.hazard_type] && (
                        <p className="text-xs text-gray-500 mt-1" data-testid="incident-type-help">
                          {INCIDENT_TYPE_HELP[formData.hazard_type]}
                        </p>
                      )}
                      </>
                    ) : (
                      <p className="text-gray-800 mt-1" data-testid="hazard-type">
                        {isObservation ? observationCategoryLabel(hazard.observation_category) : hazard.hazard_type}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-bold text-gray-700">Auto Classification</label>
                    <p className="text-gray-800 mt-1" data-testid="auto-classification">{hazard?.auto_classification || 'N/A'}</p>
                  </div>
                </div>

                {/* Lost Time Injury Flag — only shown when the incident is
                    LTI. Never rendered for observations (no injury data). */}
                {!isObservation && (editing
                  ? formData.hazard_type === 'Lost Time Injury'
                  : (hazard.is_lti || hazard.hazard_type === 'Lost Time Injury')) && (
                <div className="border-2 rounded-lg p-4 bg-orange-50 border-orange-300">
                  <label className="flex items-center">
                    {editing ? (
                      <input
                        type="checkbox"
                        name="is_lti"
                        checked
                        disabled
                        readOnly
                        className="w-5 h-5 text-[#1B5C94] border-gray-300 rounded focus:ring-[#1B5C94] focus:ring-2 disabled:bg-orange-200"
                      />
                    ) : (
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded ${hazard.is_lti ? 'bg-red-500' : 'bg-gray-300'}`}>
                        {hazard.is_lti && (
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </span>
                    )}
                    <span className="ml-3">
                      <span className="block text-sm font-bold text-gray-800">Lost Time Injury (LTI)</span>
                      <span className="block text-xs text-gray-600 mt-1">
                        {editing
                          ? 'Required because Incident Type is "Lost Time Injury"'
                          : (hazard.is_lti ? 'This incident resulted in time away from work' : 'Not classified as Lost Time Injury')}
                      </span>
                    </span>
                  </label>

                  {/* Medical Leave Days */}
                  <div className="mt-3 pt-3 border-t border-orange-300">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Medical Leave Days <span className="text-red-500">*</span>
                    </label>
                    {editing ? (
                      <input
                        type="number"
                        name="medical_leave_days"
                        step="0.5"
                        min="0"
                        value={formData.medical_leave_days || ''}
                        onChange={handleInputChange}
                        readOnly={Array.isArray(formData.injured_persons) && formData.injured_persons.length > 0}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] ${
                          Array.isArray(formData.injured_persons) && formData.injured_persons.length > 0 ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                        placeholder="Enter number of days"
                      />
                    ) : (
                      <p className="text-gray-800 font-semibold">
                        {hazard.medical_leave_days ? `${hazard.medical_leave_days} days` : 'Not specified'}
                      </p>
                    )}
                    <p className="text-xs text-gray-600 mt-1">
                      {editing && Array.isArray(formData.injured_persons) && formData.injured_persons.length > 0
                        ? 'Auto-calculated as the sum of per-person LTI days below.'
                        : 'Required for Singapore MOM iReport compliance'}
                    </p>
                  </div>
                </div>
                )}

                <div>
                  <label className="text-sm font-bold text-gray-700">Description</label>
                  {editing ? (
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="4"
                      className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                      data-testid="edit-description-input"
                    />
                  ) : (
                    <p className="text-gray-800 mt-1" data-testid="hazard-description">{hazard.description}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-bold text-gray-700">{isObservation ? 'Date of Observation' : 'Date of Incident'}</label>
                    {editing ? (
                      <input
                        name={isObservation ? 'observation_date' : 'incident_date'}
                        type="date"
                        value={(() => {
                          const v = isObservation ? formData.observation_date : formData.incident_date;
                          return v ? v.split('T')[0] : '';
                        })()}
                        onChange={handleInputChange}
                        min="2000-01-01"
                        max={todayLocalISO()}
                        className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    ) : (
                      <p className="text-gray-800 mt-1">
                        {(() => {
                          const v = isObservation ? hazard.observation_date : hazard.incident_date;
                          return v ? formatDate(v) : 'Not specified';
                        })()}
                      </p>
                    )}
                  </div>

                  {/* Time is stored separately as an "HH:MM" string
                      (incidents.incident_time / observations.observation_time),
                      not folded into the *_date timestamp. */}
                  <div>
                    <label className="text-sm font-bold text-gray-700">{isObservation ? 'Time of Observation' : 'Time of Incident'}</label>
                    {editing ? (
                      <input
                        name={isObservation ? 'observation_time' : 'incident_time'}
                        type="time"
                        value={(isObservation ? formData.observation_time : formData.incident_time) || ''}
                        onChange={handleInputChange}
                        className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    ) : (
                      <p className="text-gray-800 mt-1">
                        {(isObservation ? hazard.observation_time : hazard.incident_time) || 'Not specified'}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-bold text-gray-700">Location</label>
                    {editing ? (
                      <input
                        name="location"
                        value={formData.location || ''}
                        onChange={handleInputChange}
                        className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    ) : (
                      <p className="text-gray-800 mt-1">{hazard.location || 'N/A'}</p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-bold text-gray-700">Equipment Involved</label>
                    {editing ? (
                      <input
                        name="equipment_involved"
                        value={formData.equipment_involved || ''}
                        onChange={handleInputChange}
                        className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    ) : (
                      <p className="text-gray-800 mt-1">{hazard.equipment_involved || 'N/A'}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-bold text-gray-700">Immediate Action Taken</label>
                  {editing ? (
                    <textarea
                      name="immediate_actions"
                      value={formData.immediate_actions || ''}
                      onChange={handleInputChange}
                      rows="3"
                      className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                    />
                  ) : (
                    <p className="text-gray-800 mt-1">{hazard.immediate_actions || 'N/A'}</p>
                  )}
                </div>

                {/* Casualty Details — incident-only; observations carry no
                    injury/fatality data. */}
                {!isObservation && (
                <div className="border-2 rounded-lg p-4 bg-red-50 border-red-200">
                  <h3 className="text-sm font-bold text-red-800 mb-3">Casualty Details</h3>
                  
                  {/* Injury & Fatality Counts */}
                  <div>
                    <div className={`grid ${(editing ? formData.hazard_type : hazard.hazard_type) === 'Fatality' ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'} gap-4`}>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Number of Injuries</label>
                        {editing ? (
                          <input
                            type="number"
                            name="injury_count"
                            min="0"
                            value={formData.injury_count || ''}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                            placeholder="0"
                          />
                        ) : (
                          <p className={`text-2xl font-bold ${hazard.injury_count > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                            {hazard.injury_count || 0}
                          </p>
                        )}
                      </div>
                      {(editing ? formData.hazard_type : hazard.hazard_type) === 'Fatality' && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Number of Fatalities</label>
                        {editing ? (
                          <input
                            type="number"
                            name="fatality_count"
                            min="0"
                            value={formData.fatality_count || ''}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                            placeholder="0"
                          />
                        ) : (
                          <p className={`text-2xl font-bold ${hazard.fatality_count > 0 ? 'text-red-700' : 'text-gray-400'}`}>
                            {hazard.fatality_count || 0}
                          </p>
                        )}
                      </div>
                      )}
                    </div>

                    {/* Per-person LTI days breakdown */}
                    {editing ? (
                      Array.isArray(formData.injured_persons) && formData.injured_persons.length > 0 && (
                        <div className="mt-4 pt-3 border-t border-red-200">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">Lost-Time Days per Injured Person</h4>
                          <div className="space-y-2">
                            {formData.injured_persons.map((entry, idx) => (
                              <div key={idx} className="flex items-center gap-3">
                                <label className="text-sm text-gray-700 w-24 shrink-0" htmlFor={`iip-edit-${idx}`}>
                                  Person {idx + 1}
                                </label>
                                <input
                                  id={`iip-edit-${idx}`}
                                  type="number"
                                  step="0.5"
                                  min="0"
                                  value={entry?.lti_days ?? 0}
                                  onChange={(e) => handleInjuredPersonChange(idx, e.target.value)}
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                                  placeholder="LTI days"
                                />
                                <span className="text-xs text-gray-500">days</span>
                              </div>
                            ))}
                          </div>
                          <p className="text-xs text-gray-600 mt-2">
                            Total: {formData.injured_persons.reduce((s, p) => s + (Number(p?.lti_days) || 0), 0)} days
                          </p>
                        </div>
                      )
                    ) : (
                      Array.isArray(hazard.injured_persons) && hazard.injured_persons.length > 0 && (
                        <div className="mt-4 pt-3 border-t border-red-200">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">Lost-Time Days per Injured Person</h4>
                          <ul className="text-sm text-gray-800 space-y-1">
                            {hazard.injured_persons.map((p, idx) => (
                              <li key={p.id || idx}>
                                Person {idx + 1}: <span className="font-semibold">{p.lti_days ?? 0} days</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )
                    )}
                  </div>
                </div>
                )}
              </div>
            </div>

            {/* Photos */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-[#1B5C94] mb-4">Photos</h2>

              {/* Display existing photos */}
              {photoLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#1B5C94]"></div>
                </div>
              ) : photoUrls.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                  {photoUrls.map((photo, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={photo.url}
                        alt={`Incident ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg shadow-md cursor-pointer hover:opacity-90"
                        onClick={() => window.open(photo.url, '_blank')}
                      />
                      {editing && photo.object_key && (
                        <button
                          type="button"
                          onClick={() => handleDeletePhoto(photo.object_key)}
                          disabled={deletingPhoto === photo.object_key}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                          title="Delete photo"
                        >
                          {deletingPhoto === photo.object_key ? '...' : '×'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : !editing && (
                <p className="text-gray-500 italic">No photos uploaded</p>
              )}

              {/* Photo upload in edit mode */}
              {editing && (
                <div className="mt-4 border-t pt-4">
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    {photoUrls.length > 0 ? 'Add More Photos' : 'Upload Photos'} (up to 10 files{photoUrls.length > 0 ? `, ${10 - photoUrls.length - photoFiles.length} remaining` : ''})
                  </label>

                  {photoPreviews.length > 0 ? (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600">{photoPreviews.length} file(s) selected</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {photoPreviews.map((photo, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={photo.preview}
                              alt={`Preview ${index + 1}`}
                              className="w-full h-32 object-cover rounded-lg shadow-md"
                            />
                            <button
                              type="button"
                              onClick={() => removePhotoPreview(index)}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Remove"
                            >
                              ×
                            </button>
                            <p className="text-xs text-gray-500 mt-1 truncate">{photo.name}</p>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handlePhotoSave}
                          disabled={uploadingPhoto}
                          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                        >
                          {uploadingPhoto ? 'Uploading...' : `Save ${photoPreviews.length} Photo(s)`}
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelPhotoUpload}
                          disabled={uploadingPhoto}
                          className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.gif"
                      multiple
                      onChange={handlePhotoUpload}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                    />
                  )}
                  <p className="text-xs text-gray-500 mt-2">Supported formats: JPG, PNG, GIF. Max size: 10MB per file</p>
                </div>
              )}
            </div>

            {/* Documents */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-[#1B5C94] mb-4">Documents & Reports</h2>
              <p className="text-sm text-gray-500 mb-4">Investigation reports, 8D reports, and supporting documents</p>

              {/* Existing documents */}
              {Array.isArray(hazard?.documents) && hazard.documents.length > 0 ? (
                <div className="space-y-2 mb-4">
                  {hazard.documents.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-xl flex-shrink-0">{getDocIcon(doc.file_type)}</span>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-800 truncate">{doc.filename}</div>
                          <div className="text-xs text-gray-500">
                            {doc.file_type?.toUpperCase()}
                            {doc.size_bytes && ` · ${(doc.size_bytes / 1024).toFixed(0)} KB`}
                            {doc.uploaded_by_name && ` · ${doc.uploaded_by_name}`}
                            {doc.uploaded_at && ` · ${doc.uploaded_at.substring(0, 10)}`}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleDocDownload(index, doc.filename)}
                          className="text-[#1B5C94] hover:text-[#3AC0DB] text-sm font-semibold"
                        >
                          Download
                        </button>
                        {editing && hazard.status !== 'Closed' && (
                          <button
                            onClick={() => handleDocDelete(index)}
                            disabled={docDeleting === index}
                            className="text-red-500 hover:text-red-700 text-sm font-semibold disabled:opacity-50"
                          >
                            {docDeleting === index ? '...' : 'Delete'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                !editing && <p className="text-gray-500 italic mb-4">No documents attached</p>
              )}

              {/* Upload in edit mode */}
              {editing && hazard.status !== 'Closed' && (
                <div className="border-t pt-4">
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Upload Documents (PDF, Word, Excel, PowerPoint, Images — max 25MB each)
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif"
                    multiple
                    onChange={handleDocUpload}
                    disabled={docUploading}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] disabled:opacity-50"
                  />
                  {docUploading && (
                    <div className="flex items-center gap-2 mt-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-[#1B5C94]"></div>
                      <span className="text-sm text-gray-600">Uploading...</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Root Cause Category Tags */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-[#1B5C94] mb-4">Root Cause Categories</h2>
              {editing ? (
                <div className="flex flex-wrap gap-2">
                  {ROOT_CAUSE_TAGS.map(tag => {
                    const tags = Array.isArray(formData.tags) ? formData.tags : [];
                    const isSelected = tags.includes(tag);
                    return (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            tags: isSelected
                              ? (prev.tags || []).filter(t => t !== tag)
                              : [...(prev.tags || []), tag]
                          }));
                        }}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                          isSelected
                            ? 'bg-[#1B5C94] text-white border-[#1B5C94]'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-[#1B5C94] hover:text-[#1B5C94]'
                        }`}
                      >
                        {isSelected ? '✓ ' : ''}{tag}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {Array.isArray(hazard?.tags) && hazard.tags.length > 0 ? (
                    hazard.tags.map(tag => (
                      <span key={tag} className="px-3 py-1.5 rounded-full text-sm font-medium bg-[#1B5C94] text-white">
                        {tag}
                      </span>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm">No root cause categories assigned</p>
                  )}
                </div>
              )}
            </div>

            {/* Closure Details — shown when incident is closed */}
            {hazard?.status === 'Closed' && (hazard?.close_notes || closureFileUrls.length > 0) && (
              <div className="bg-green-50 rounded-xl shadow-md p-6 border border-green-200">
                <h2 className="text-xl font-bold text-green-800 mb-4">Closure Details</h2>
                {hazard.close_notes && (
                  <div className="mb-4">
                    <span className="text-sm font-bold text-gray-700">Closure Notes</span>
                    <p className="text-gray-800 mt-1 whitespace-pre-wrap">{hazard.close_notes}</p>
                  </div>
                )}
                {closureFileUrls.length > 0 && (
                  <div>
                    <span className="text-sm font-bold text-gray-700">Closure Attachments</span>
                    <div className="mt-2 space-y-2">
                      {closureFileUrls.map((file, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-white rounded-lg p-3 border border-green-200">
                          <span className="text-gray-700 text-sm flex-1 truncate">{file.filename}</span>
                          {file.download_url ? (
                            <a
                              href={file.download_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#1B5C94] hover:text-[#3AC0DB] text-sm font-semibold"
                            >
                              Download
                            </a>
                          ) : (
                            <span className="text-gray-400 text-sm">Unavailable</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Linked CAPAs */}
            {linkedCapas.length > 0 && (
              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-xl font-bold text-[#1B5C94] mb-4">Linked CAPAs ({linkedCapas.length})</h2>
                <div className="space-y-3">
                  {linkedCapas.map(capa => (
                    <div key={capa.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono text-gray-500">{capa.capa_number || capa.id.substring(0, 8)}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            capa.capa_type === 'corrective' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                          }`}>
                            {capa.capa_type === 'corrective' ? 'Corrective' : 'Preventive'}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            capa.status === 'Closed' ? 'bg-green-100 text-green-700' :
                            capa.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {capa.status}
                          </span>
                        </div>
                        <p className="font-semibold text-gray-800 cursor-pointer hover:text-[#1B5C94]"
                           onClick={() => navigate(`/capa/${capa.id}`)}>
                          {capa.title}
                        </p>
                        {capa.responsible_person && (
                          <p className="text-sm text-gray-500">Responsible: {capa.responsible_person}</p>
                        )}
                      </div>
                      {!editing && hazard?.status !== 'Closed' && hasPermission('capa', 'update') && capa.link_id && (
                        <button
                          onClick={() => handleUnlinkCapa(capa.id, capa.link_id)}
                          className="text-red-400 hover:text-red-600 text-xs ml-3"
                          title="Unlink CAPA"
                        >
                          Unlink
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Link CAPA Modal */}
            {showLinkCapaModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl shadow-xl p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
                  <h3 className="text-xl font-bold text-[#1B5C94] mb-4">Link Existing CAPA</h3>
                  {availableCapas.length === 0 ? (
                    <p className="text-gray-500 text-sm">No available CAPAs to link. All CAPAs are either already linked or none exist.</p>
                  ) : (
                    <div className="space-y-2">
                      {availableCapas.map(capa => (
                        <div key={capa.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200 hover:border-purple-300">
                          <div className="flex-1">
                            <p className="font-semibold text-gray-800 text-sm">{capa.title}</p>
                            <div className="flex gap-2 mt-1">
                              <span className="text-xs text-gray-500">{capa.capa_number || ''}</span>
                              <span className="text-xs text-gray-500">{capa.capa_type}</span>
                              <span className="text-xs text-gray-500">{capa.status}</span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleLinkCapa(capa.id)}
                            disabled={linkingCapa}
                            className="px-3 py-1 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50"
                          >
                            Link
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={() => setShowLinkCapaModal(false)}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Management Actions */}
            {(user?.role === 'admin' || user?.role === 'Admin' || user?.role === 'hse_manager' || user?.role === 'HSE Manager') && (
              <div className="bg-white rounded-xl shadow-md p-6">
                {/* With every RCA field switched off for the tenant only the
                    Target Date row remains, so the heading drops "Root Cause". */}
                <h2 className="text-xl font-bold text-[#1B5C94] mb-4">{anyRcaFieldShown ? <>Root Cause &amp; Actions</> : 'Actions'}</h2>

                {/* AI Analysis — placed at the top of this card so it sits
                    directly above the fields it populates (Root Cause,
                    Corrective Action, Recommended Actions). Hidden once those
                    fields are filled, or when the report is in a state where
                    AI analysis isn't meaningful (Draft/Closed). Only the
                    tenant's visible RCA fields count — never offered for
                    hidden ones alone. */}
                {!editing && rcaAiPending && hazard.status !== 'Closed' && hazard.status !== 'Draft' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-700 mb-3">AI analysis was not generated for this report. Run it to populate {shownRcaLabels} below.</p>
                    <button
                      onClick={handleGenerateAIAnalysis}
                      disabled={generatingAI}
                      className="bg-[#1B5C94] text-white font-semibold py-2 px-4 rounded-lg hover:bg-[#164A7B] transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {generatingAI ? (
                        <>
                          <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Generating AI Analysis...
                        </>
                      ) : (
                        'Generate AI Analysis'
                      )}
                    </button>
                  </div>
                )}
                {!editing && hazard.status === 'Draft' && rcaAiPending && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-gray-600">AI analysis becomes available once the incident is submitted (status moves from Draft to Open).</p>
                  </div>
                )}

                <div className="space-y-4">
                  {rcaFields.root_cause && (
                  <div>
                    <label className="text-sm font-bold text-gray-700">Root Cause</label>
                    {editing ? (
                      <select
                        name="root_cause"
                        value={formData.root_cause || ''}
                        onChange={handleInputChange}
                        className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                      >
                        <option value="">Select root cause</option>
                        <option>Unsafe act</option>
                        <option>Unsafe condition</option>
                        <option>Equipment failure</option>
                        <option>Lack of training</option>
                        <option>Poor maintenance</option>
                        <option>Other</option>
                      </select>
                    ) : (
                      <p className="text-gray-800 mt-1">{hazard.root_cause || 'Not specified'}</p>
                    )}
                  </div>
                  )}

                  {rcaFields.corrective_action && (
                  <div>
                    <label className="text-sm font-bold text-gray-700">Corrective Action</label>
                    {editing ? (
                      <textarea
                        name="corrective_action"
                        value={formData.corrective_action || ''}
                        onChange={handleInputChange}
                        rows="3"
                        className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    ) : (
                      <p className="text-gray-800 mt-1">{hazard.corrective_action || 'Not specified'}</p>
                    )}
                  </div>
                  )}

                  {rcaFields.recommended_actions && (
                  <div>
                    <label className="text-sm font-bold text-gray-700">Recommended Future Actions</label>
                    {editing ? (
                      <div>
                        <textarea
                          name="recommended_actions"
                          value={formData.recommended_actions || ''}
                          onChange={handleInputChange}
                          rows="3"
                          className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                          placeholder="Enter preventive measures separated by semicolons"
                        />
                        <p className="text-xs text-gray-500 mt-1">Separate multiple actions with semicolons (;)</p>
                      </div>
                    ) : (
                      <div className="mt-1">
                        {hazard.recommended_actions && hazard.recommended_actions.length > 0 ? (
                          <ul className="list-disc list-inside space-y-1">
                            {hazard.recommended_actions.map((action, idx) => (
                              <li key={idx} className="text-gray-800 text-sm">{action}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-gray-800">Not specified</p>
                        )}
                      </div>
                    )}
                  </div>
                  )}

                  {/* Responsible Person field hidden by product decision 2026-03-12 — re-enable when ready */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-bold text-gray-700">Target Date</label>
                      {editing ? (
                        <input
                          name="target_date"
                          type="date"
                          value={formData.target_date ? formData.target_date.split('T')[0] : ''}
                          onChange={handleInputChange}
                          min={new Date().toISOString().split('T')[0]}
                          max="2099-12-31"
                          className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                        />
                      ) : (
                        <p className="text-gray-800 mt-1">{hazard.target_date ? formatDate(hazard.target_date) : 'Not set'}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Status & Metadata */}
          <div className="space-y-6">
            {/* Status Card */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Status</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-bold text-gray-700">Current Status</label>
                  {editing ? (
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                      data-testid="edit-status-select"
                    >
                      {allowedTransitions(formData.status).map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  ) : (
                    <div className="mt-2">
                      <span className={`inline-block px-4 py-2 rounded-lg text-sm font-bold border-2 ${getStatusColor(hazard.status)}`} data-testid="hazard-status">
                        {hazard.status}
                      </span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-sm font-bold text-gray-700">Severity</label>
                  {editing ? (
                    <select
                      name="severity"
                      value={formData.severity}
                      onChange={handleInputChange}
                      className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                      data-testid="edit-severity-select"
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Critical">Critical</option>
                    </select>
                  ) : (
                    <div className="mt-2">
                      <span className={`inline-block px-4 py-2 rounded-lg text-sm font-bold border-2 ${getSeverityColor(hazard.severity)}`} data-testid="hazard-severity">
                        {hazard.severity}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Assignment Card */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Assignment</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-bold text-gray-700">Assigned To</label>
                  {editing && isAdminOrHSE ? (
                    <select
                      name="assignee"
                      value={formData.assignee || ''}
                      onChange={handleInputChange}
                      className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                    >
                      <option value="">Select assignee</option>
                      {/* Keep the currently-stored assignee selectable even when
                          they aren't in the department-scoped list (legacy or
                          cross-department records). Without this the select
                          renders blank and silently reassigns on save. */}
                      {formData.assignee &&
                        !users.some(u => u.name === formData.assignee) && (
                          <option value={formData.assignee}>
                            {formData.assignee} (current)
                          </option>
                        )}
                      {users.map(u => (
                        <option key={u.id} value={u.name}>
                          {u.name}{u.department ? ` — ${u.department}` : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-gray-800 font-semibold mt-1">{hazard.assignee || 'Not assigned'}</p>
                  )}
                </div>

                <div>
                  <label className="text-sm font-bold text-gray-700">Due Date</label>
                  {editing ? (
                    <input
                      name="due_date"
                      type="date"
                      value={formData.due_date ? formData.due_date.split('T')[0] : ''}
                      onChange={handleInputChange}
                      min={new Date().toISOString().split('T')[0]}
                      max="2099-12-31"
                      className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                    />
                  ) : (
                    <p className="text-gray-800 mt-1">{hazard.due_date ? formatDate(hazard.due_date) : 'Not set'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Metadata Card */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Details</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="text-gray-600 font-semibold">{entityLabel} ID:</span>
                  <p className="text-gray-800 mt-1 text-xs font-mono">
                    {isObservation
                      ? (hazard.observation_number || hazard.id)
                      : (hazard.incident_number || hazard.id)}
                  </p>
                  {/* Legacy records split out of Incident Management keep a
                      pointer to their original incident number. */}
                  {isObservation && hazard.legacy_incident_number && (
                    <p className="text-xs text-gray-500 mt-1">
                      Migrated from {hazard.legacy_incident_number}
                    </p>
                  )}
                </div>
                <div>
                  <span className="text-gray-600 font-semibold">Reported By:</span>
                  <p className="text-gray-800 mt-1">
                    {/* Anonymous QR submissions store NULL `reported_by` and
                        may have a blank `reported_by_name` if the public
                        user didn't fill in a name. Fall back to whichever
                        contact info we have, then "Anonymous report" — it's
                        more honest than "Unknown" for a QR/public flow. */}
                    {hazard.reported_by_name
                      || hazard.reporter_email
                      || hazard.reporter_mobile
                      || (hazard.report_source === 'public_qr' || hazard.report_source === 'qr'
                            ? 'Anonymous report'
                            : (hazard.reported_by || 'Unknown'))}
                  </p>
                </div>
                {hazard.reporter_email && (
                  <div>
                    <span className="text-gray-600 font-semibold">Reporter Email:</span>
                    <p className="text-gray-800 mt-1">{hazard.reporter_email}</p>
                  </div>
                )}
                {hazard.reporter_mobile && (
                  <div>
                    <span className="text-gray-600 font-semibold">Reporter Mobile:</span>
                    <p className="text-gray-800 mt-1">{hazard.reporter_mobile}</p>
                  </div>
                )}
                <div>
                  <span className="text-gray-600 font-semibold">Date Reported:</span>
                  <p className="text-gray-800 mt-1">
                    {/* Backend column is `reported_date` (see
                        _RESPONSE_COLUMNS_SQL in backend/routes/incidents.py).
                        The previous code read a non-existent
                        `date_reported` field, so this row always rendered
                        "Not specified". Keep both keys as a safety net for
                        any caller that still emits the legacy alias. */}
                    {(() => {
                      const v = hazard.reported_date || hazard.date_reported;
                      return v ? formatDate(v) : 'Not specified';
                    })()}
                  </p>
                </div>
                {hazard.closed_date && (
                  <div>
                    <span className="text-gray-600 font-semibold">Closed Date:</span>
                    <p className="text-gray-800 mt-1">
                      {formatDateTime(hazard.closed_date)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
          </>
        )}
        {activeTab === 'audit' && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <AuditTrailViewer
              // Post-split, this page is canonically the incident detail
              // view. The backend's /api/v1/audit/incident/{id}/history
              // endpoint unions target_type IN ('incident', 'hazard') so
              // pre-split audit entries (logged as 'hazard') still appear.
              // For observations, the same viewer reads the 'observation'
              // entity type (whitelisted in services/audit_logging.py).
              entityType={isObservation ? 'observation' : 'incident'}
              entityId={id}
              title={isObservation ? 'Observation Audit History' : 'Incident Audit History'}
            />
          </div>
        )}
        {activeTab === '8d' && !isObservation && (
          <EightDReport hazardId={id} />
        )}
      </div>

      {/* PDF Export Progress Modal */}
      <PDFExportModal 
        isOpen={showPDFModal}
        onClose={() => setShowPDFModal(false)}
        hazardId={id}
      />

      {/* RCA Modals */}
      {showFiveWhy && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
          <div className="min-h-screen flex items-center justify-center p-4">
            <div className="max-w-4xl w-full my-8">
              <FiveWhyAnalysis hazardId={id} apiBase={apiBase} onClose={() => setShowFiveWhy(false)} />
            </div>
          </div>
        </div>
      )}

      {showFishbone && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
          <div className="min-h-screen flex items-center justify-center p-4">
            <div className="max-w-6xl w-full my-8">
              <FishboneAnalysis hazardId={id} apiBase={apiBase} canExportPdf={!isObservation} onClose={() => setShowFishbone(false)} />
            </div>
          </div>
        </div>
      )}

      {showWhyWhy && !isObservation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
          <div className="min-h-screen flex items-center justify-center p-4">
            <div className="max-w-4xl w-full my-8">
              <WhyWhyAnalysis hazardId={id} apiBase={apiBase} onClose={() => setShowWhyWhy(false)} />
            </div>
          </div>
        </div>
      )}

      {/* Statutory Report Form */}
      {/* Closure Modal */}
      {showClosureModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Close {entityLabel}</h2>
              <button onClick={() => setShowClosureModal(false)} className="text-gray-500 hover:text-gray-700">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Closure Comments (Optional)</label>
                <textarea
                  value={closureNotes}
                  onChange={(e) => setClosureNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94] focus:outline-none"
                  placeholder="Add any closure remarks or observations..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Closure Files (Optional, max 5)
                </label>
                <input
                  type="file"
                  onChange={handleClosureFileChange}
                  multiple
                  accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx"
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-[#1B5C94] hover:file:bg-blue-100"
                />
                {closureFiles.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {closureFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 rounded px-3 py-1 text-sm">
                        <span className="truncate">{file.name}</span>
                        <button onClick={() => removeClosureFile(idx)} className="text-red-500 hover:text-red-700 ml-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6 pt-4 border-t">
              <button
                onClick={handleClosureSubmit}
                disabled={closingIncident}
                className="flex-1 bg-red-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {closingIncident ? 'Closing...' : 'Confirm Close'}
              </button>
              <button
                onClick={() => setShowClosureModal(false)}
                disabled={closingIncident}
                className="px-6 py-2 bg-gray-200 text-gray-700 font-bold rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showStatutoryForm && hazard && (
        <StatutoryReportForm
          hazard={hazard}
          forms={showStatutoryForm.rows}
          onClose={() => setShowStatutoryForm(null)}
        />
      )}
    </div>
      </div>
  );
};

export default HazardDetail;